import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  AccordionDetails,
  Grid,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import PageContainer from "../../../components/pageContainer";
import { formatDate } from "../../../utils/datetimeUtils";
import { PATHS } from "../../../routes/paths";
import { StyledAccordion, StyledAccordionSummary, StyledTableCell } from "../../../styles/commonStyles";
import { ExpandMore } from "@mui/icons-material";
import {
  getManufacturerAdminManufacturersManufacturerIdGet,
  updateManufacturerAdminManufacturersManufacturerIdPatch,
  deleteManufacturerAdminManufacturersManufacturerIdDelete
} from "../../../client/services.gen";
import { ManufacturerResponse, ManufacturerUpdate } from "../../../client/types.gen";

const ManufactureDetail: FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<ManufacturerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchManufacturer = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getManufacturerAdminManufacturersManufacturerIdGet({ manufacturerId: Number(id) });
      setData(response);
    } catch (err) {
      setError('製造メーカーの取得に失敗しました');
      console.error('Failed to fetch manufacturer:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManufacturer();
  }, [id]);

  const handleSave = async () => {
    if (!data || !id) return;

    setSaving(true);
    setError(null);
    try {
      const updateData: ManufacturerUpdate = {
        name: data.name
      };

      const response = await updateManufacturerAdminManufacturersManufacturerIdPatch({
        manufacturerId: Number(id),
        requestBody: updateData
      });

      setData(response);
      setIsEditing(false);
    } catch (err) {
      setError('製造メーカーの更新に失敗しました');
      console.error('Failed to update manufacturer:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setSaving(true);
    setError(null);
    try {
      await deleteManufacturerAdminManufacturersManufacturerIdDelete({ manufacturerId: Number(id) });
      navigate(PATHS.MANUFACTURER_LIST);
    } catch (err) {
      setError('製造メーカーの削除に失敗しました');
      console.error('Failed to delete manufacturer:', err);
      setSaving(false);
    }
    setDeleteDialogOpen(false);
  };

  const handleCancel = () => {
    fetchManufacturer();
    setIsEditing(false);
    setError(null);
  };

  if (loading) {
    return (
      <PageContainer>
        <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <CircularProgress />
        </Grid>
      </PageContainer>
    );
  };

  if (!data) {
    return (
      <PageContainer>
        <Alert severity="error">製造メーカーが見つかりません</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ヘッダー */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="div">メーカー詳細</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditing ? (
            <>
              <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                キャンセル
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={20} /> : '保存'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
              >
                削除
              </Button>
              <Button variant="contained" onClick={() => setIsEditing(true)}>
                編集
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* 基本情報 */}
      <StyledAccordion defaultExpanded>
        <StyledAccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">基本情報</Typography>
        </StyledAccordionSummary>
        <AccordionDetails>
          <Table>
            <TableBody>
            <TableRow>
              <StyledTableCell component="th" scope="row" className="item-label">
                メーカー名
              </StyledTableCell>
              <TableCell>
                {isEditing ? (
                  <TextField
                    value={data.name}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                  />
                ) : (
                  data.name
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell component="th" scope="row" className="item-label">
                作成日
              </StyledTableCell>
              <TableCell>{formatDate(data.created_at)}</TableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell component="th" scope="row" className="item-label">
                更新日
              </StyledTableCell>
              <TableCell>{formatDate(data.updated_at)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </AccordionDetails>
    </StyledAccordion>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{data.name}」を削除してもよろしいですか？
            この操作は取り消せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleDelete} color="error" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default ManufactureDetail;